import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";

// Import images
import drivingLesson from "../PhotosGallery/best-rijschool-oost-souburg-rijles.jpg";
import instructor from "../PhotosGallery/rijinstructeur-best-rijschool.jpg";
import studentLesson from "../PhotosGallery/leerling-rijles-oost-souburg.jpg";
import carLesson from "../PhotosGallery/autorijles-best-rijschool.jpg";
import licence from "../PhotosGallery/rijbewijs-halen-oost-souburg.jpg";
import manual from "../PhotosGallery/schakelrijles-best-rijschool.jpg";
import automatic from "../PhotosGallery/automaat-rijles-oost-souburg.jpg";
import area from "../PhotosGallery/rijschool-vlissingen-middelburg.jpg";

const images = [

{
src:drivingLesson,
alt:"Rijles bij Best Rijschool Oost-Souburg"
},

{
src:instructor,
alt:"Rijinstructeur Best Rijschool"
},

{
src:studentLesson,
alt:"Leerling tijdens autorijles in Oost-Souburg"
},

{
src:carLesson,
alt:"Autorijles Best Rijschool Zeeland"
},

{
src:licence,
alt:"Rijbewijs halen met Best Rijschool"
},

{
src:manual,
alt:"Schakelrijles bij Best Rijschool"
},

{
src:automatic,
alt:"Automaat rijles Oost-Souburg"
},

{
src:area,
alt:"Rijschool actief in Oost-Souburg Vlissingen Middelburg"
}

];

// Grid layout for images
const GalleryContainer = styled.div`
  display: grid;
  /* Ordered rows expand from two phone columns to five desktop columns. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(1rem, 3vw, 1.5rem);
  padding: clamp(1rem, 4vw, 2.5rem);
  min-height: 100vh;
  color: darkgoldenrod;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  
   

`;

// Circle style for each image in the grid
const Thumbnail = styled.img`
  width: 100%;
  max-width: 15rem;
  aspect-ratio: 1;
  height: auto;
  justify-self: center;
  object-fit: cover;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.3s;
  &:hover {
    transform: scale(1.2); // Adjusted scale
  }
`;

// Overlay for enlarged image view
interface OverlayProps {
  $isOpen: boolean;
}

const Overlay = styled.div<OverlayProps>`
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// Styling for enlarged image container
const LargeImageContainer = styled.div`
  position: relative;
  width: min(96vw, 52rem);
  max-height: 88vh;
  display: grid;
  grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;

`;

// Styling for enlarged image
const imageReveal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const LargeImage = styled.img`
  grid-column: 2;
  width: 100%;
  height: auto;
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 10px;
  /* A new key remounts this image for a smooth transition on every change. */
  animation: ${imageReveal} 280ms ease-out both;
`;

// Arrow button styles
const ArrowButton = styled.button<{ direction: string }>`
  /* Controls stay beside the photo, including on narrow phone screens. */
  position: static;
  grid-column: ${({ direction }) => (direction === "left" ? 1 : 3)};
  background: darkgoldenrod;
  border: none;
  font-size: 1.75rem;
  color: #2e2e2e;
  cursor: pointer;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border-radius: 50%;
  &:hover {
    background: gold;
  }

`;

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Handlers for navigation and closing overlay
  const openOverlay = (index: number) => {
    setCurrentIndex(index);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  return (
    <div>
      <GalleryContainer>
{images.map((image,index)=>(

<Thumbnail

key={index}

src={image.src}

alt={t("gallery.imageAlt", { number: index + 1 })}

onClick={()=>openOverlay(index)}

/>

))}
      </GalleryContainer>

      {/* Overlay for enlarged view */}
      <Overlay $isOpen={isOverlayOpen} onClick={closeOverlay}>
        <LargeImageContainer onClick={(e) => e.stopPropagation()}>
          <ArrowButton direction="left" onClick={handlePrev} aria-label={t("gallery.previous")}>
            &lt;
          </ArrowButton>
          <LargeImage
            key={currentIndex}
            src={images[currentIndex].src}
            alt={t("gallery.imageAlt", { number: currentIndex + 1 })}
          />
          <ArrowButton direction="right" onClick={handleNext} aria-label={t("gallery.next")}>
            &gt;
          </ArrowButton>
        </LargeImageContainer>
      </Overlay>
    </div>
  );
};

export default Gallery;
